<template>
  <div class="artist-card">
    <router-link
      :to="artistLink"
      @click.native="logClick"
      class="artist-card-link card-animation-on-hover-no-bg"
    >
      <div class="artist-card-header">
        <img
          v-if="displayImage"
          v-lazy="lazyImageSrc"
          :style="{ borderColor: secondaryColor }"
          class="artist-image"
          :alt="eventTitle"
          height="80px"
          width="80px"
        />
        <div class="artist-info">
          <span class="artist-name">{{ eventTitle }}</span>
          <br />
          <small class="event-location-line">{{ eventLocationLineDisplay }}</small>
          <p v-if="event.metadata && event.metadata.start_time" class="event-time">
            {{ event.metadata.start_time }} hrs -
            {{ getMonthShort(fullEventDateTime) }}
            {{ getDayNumeric(fullEventDateTime) }},
            {{ getYear(fullEventDateTime) }}
          </p>
          <p v-else-if="event.metadata && event.metadata.date" class="event-time">
            {{ getDay(event.metadata.date + 'T00:00') }},
            {{ getMonthShort(event.metadata.date + 'T00:00') }}
            {{ getDayNumeric(event.metadata.date + 'T00:00') }}
          </p>
        </div>
      </div>
    </router-link>

    <div v-if="showContent && event.content" class="artist-card-content">
      <hr />
      <div v-html="event.content" class="content-html"></div>

    </div>

    <div class="actions-footer text-center" v-if="eventLink.path || eventLink.name">
      <router-link :to="eventLink" @click.native="logClick" class="btn btn-sm">
        Event Details & Tickets
      </router-link>
    </div>
  </div>
</template>

<script>
import { eventDateMixin } from '@/common/eventDateMixin'; // Assuming path is correct

export default {
  name: 'ArtistCard',
  mixins: [eventDateMixin],
  props: {
    event: {
      type: Object,
      default: () => ({
        title: 'N/A', // Provide a default title
        metadata: { image_url: '', date: null, start_time: null, event_link: '', artist_link: '' },
        content: ''
      })
    },
    image: { // Prop for explicit image URL, overrides event.metadata.image_url
      type: String,
      default: ''
    },
    showContent: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      // expandTab: false, // This was in original but not used in template snippet
      secondaryColor: 'rgb(255, 141, 114)', // Example color, can be a prop
    };
  },
  computed: {
    displayImage() {
      return this.image || (this.event && this.event.metadata && this.event.metadata.image_url);
    },
    lazyImageSrc() {
      const imgSrc = this.image || (this.event && this.event.metadata && this.event.metadata.image_url);
      return imgSrc ? `${imgSrc}?q=&w=80&h=80&fit=clamp&auto=format` : ''; // Increased size slightly for clarity
    },
    eventTitle() {
      if (this.event && this.event.title && typeof this.event.title === 'string') {
        return this.event.title.split('@')[0].trim();
      }
      return 'Untitled Event';
    },
    eventLocationPart() {
      if (this.event && this.event.title && typeof this.event.title === 'string') {
        const parts = this.event.title.split('@');
        if (parts.length > 1 && parts[1]) {
          return parts[1].split('|')[0].trim();
        }
      }
      return '';
    },
    eventBrandPart() {
      if (this.event && this.event.title && typeof this.event.title === 'string') {
        const parts = this.event.title.split('@');
        if (parts.length > 1 && parts[1]) {
          const locationAndBrand = parts[1].split('|');
          if (locationAndBrand.length > 1 && locationAndBrand[1]) {
            return locationAndBrand[1].trim();
          }
        }
      }
      return '';
    },
    eventLocationLineDisplay() {
      let line = this.eventLocationPart;
      if (this.eventBrandPart) {
        line += (line ? ' @ ' : '') + this.eventBrandPart; // Use brand as venue if location is empty
      }
      return line || 'Location TBD';
    },
    fullEventDateTime() {
      if (this.event && this.event.metadata && this.event.metadata.date) {
        return `${this.event.metadata.date}T${this.event.metadata.start_time || '00:00:00'}`;
      }
      return new Date().toISOString(); // Fallback, though ideally should not be needed if date is present
    },
    eventLink() {
      // Ensure a valid path for router-link, even if it's just to home
      const path = this.event && this.event.metadata && this.event.metadata.event_link
        ? this.event.metadata.event_link
        : { name: 'Home' }; // Fallback to a named route 'Home' or '/'
      return typeof path === 'string' ? { path } : path;
    },
    artistLink() {
      if (this.event && this.event.metadata && this.event.metadata.artist_link) {
        return { path: this.event.metadata.artist_link };
      }
      return null;
    }
  },
  methods: {
    // expand() { // This was in original but not used in template snippet
    //   this.expandTab = !this.expandTab;
    // },
    logClick() {
      // Using Rollbar instance if available globally or injected
      if (this.$rollbar && this.event && this.event.title) {
        this.$rollbar.info('Artist Card Clicked', { eventTitle: this.event.title, link: this.eventLink });
      } else {
        console.log('Artist Card Clicked:', this.event ? this.event.title : 'N/A', 'Link:', this.eventLink);
      }
    }
  }
};
</script>

<style scoped>
.artist-card {
  background-color: #322e2e;
  padding: 10px; /* Reduced overall padding */
  margin: 5px;
  border-radius: 8px; /* Added border-radius for consistency */
  color: white;
  text-align: left;
}

.artist-card-link {
  display: block; /* Make the link a block for easier layout */
  text-decoration: none;
  color: inherit;
  padding: 15px 10px 10px 10px; /* Added some padding to the link itself */
}

.artist-card-header {
  display: flex; /* Use flexbox for image and text alignment */
  align-items: center; /* Vertically align items in the center */
  padding-bottom: 10px; /* Space below header before content/actions */
}

.artist-image {
  /* float:left; */ /* Replaced by flexbox */
  margin-right: 15px; /* Adjusted margin */
  /* margin-left: 20px; */ /* Removed, handled by parent padding */
  box-shadow: 0px 5px 10px 0px rgba(0, 0, 0, 0.5); /* Softer shadow */
  border-radius: 50%; /* Make it circular */
  border: 2px solid; /* Border width, color from inline style */
  object-fit: cover; /* Ensure image covers the area well */
}

.artist-info {
  /* display: inline; */ /* Replaced by flex item */
  /* vertical-align: top; */ /* Handled by flex align-items: center */
  /* position:relative ; */ /* Not needed for this layout */
  line-height: 1.2rem; /* Adjusted line-height for better readability */
  flex-grow: 1; /* Allow info to take remaining space */
}

.artist-name {
  font-weight: 700 !important;
  font-size: 1.1rem; /* Slightly increased for emphasis */
  display: block; /* Ensure it takes its own line if text wraps */
  margin-bottom: 4px;
}

.event-location-line {
  /* margin-top: -50px; */ /* Removed, layout handled by flex and line-height */
  color: silver;
  text-transform: uppercase;
  font-size: 0.75rem; /* Slightly adjusted */
  display: block;
  margin-bottom: 6px;
}

.event-time {
  font-size: 0.8rem; /* Consistent font size for time */
  margin-top: 4px; /* Space above time */
  margin-bottom: 0;
  color: #f0f0f0; /* Brighter color for time */
}

.artist-card-content {
  margin-top: 0; /* Reduced margin-top */
  padding: 10px;
  background-color: #3f3a3a; /* Slightly different background for content section */
  border-radius: 0 0 6px 6px; /* Rounded bottom corners if it's the last element */
}

.artist-card-content hr {
  border-color: #4a4a4a; /* Darker hr */
  margin-top: 10px;
  margin-bottom: 10px;
}

.content-html {
  font-size: 0.85rem;
  line-height: 1.5;
  color: #e0e0e0;
}
.content-html >>> p { /* Style content coming from v-html */
  margin-bottom: 0.5em;
}
.content-html >>> a {
  color: #ffa781; /* Example link color for v-html content */
}


.actions-footer {
  padding-top: 10px;
  margin-top: 5px; /* Space above the action buttons */
}

.btn.btn-sm {
  background-color: #555; /* Example button color */
  color: white;
  padding: 6px 12px;
  border-radius: 4px;
  text-decoration: none;
  font-size: 0.8rem;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.btn.btn-sm:hover {
  background-color: #666;
}

/* Card hover animation (if you still want it on the card itself) */
.artist-card:hover {
  /* transform: translateY(-3px); */
  /* box-shadow: 0 4px 12px rgba(0,0,0,0.25); */
}

/* No background version of hover (applied to link) */
.card-animation-on-hover-no-bg:hover {
  /* Optional: add a subtle effect if desired, e.g., slight opacity change on text */
}
</style>
